import { useIntersectionObserver, useSyncedRef, useThrottledCallback } from "@react-hookz/web";
import { useAtomValue, useSetAtom } from "jotai";
import first from "lodash/first";
import { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { useSCMPExperimentCookie } from "~/components/home/hooks";
import { useMixpanel } from "~/components/mixpanel";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { Event as GA4Event } from "~/components/tracking/google-analytics-4/types";
import * as gtm from "~/components/tracking/google-tag-manager/apis";
import { setGTMCustomDimension } from "~/components/tracking/google-tag-manager/apis";
import { accountAtom } from "~/lib/account/atoms";
import { useCanonicalUrl, useDetectAdBlock, useMyNews } from "~/lib/hooks";
import { rosettaAtom } from "~/lib/rosetta";
import { isBotUserAgent } from "~/lib/utils";
import { useIsAutoRefreshed } from "~/pages/home/hooks";

import type { TrackPageViewFunction } from "./atoms";
import { trackingAtom } from "./atoms";

export * from "./atoms";

type PendingPageView = {
  canonicalURL: string;
  data?: Record<string, unknown>;
};

const useExperimentCustomDimension = () => {
  const { value: scmpExperiment } = useSCMPExperimentCookie();

  const result = useMemo(() => {
    if (scmpExperiment) {
      return {
        scmpExperiment: `10005-${scmpExperiment}`,
      };
    }
    return null;
  }, [scmpExperiment]);

  return result;
};

export const useInitTrackingSetup = () => {
  const { canonicalUrl: canonicalURL } = useCanonicalUrl();
  const isBot = isBotUserAgent();
  const scmpExperiment = useExperimentCustomDimension();

  useLayoutEffect(() => {
    const data = {
      canonicalURL,
      isBot,
      ...scmpExperiment,
    };
    // Use for the events before page view sent
    setGTMCustomDimension(data);
  }, [canonicalURL, isBot, scmpExperiment]);
};

export const useTrackingSetup = () => {
  const asyncAdBlockState = useDetectAdBlock();
  const { canonicalUrl: canonicalURL } = useCanonicalUrl();

  const { isLoggedIn, loginType } = useAtomValue(accountAtom);
  const isBot = isBotUserAgent();
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const asyncMixpanelState = useMixpanel();
  const { hasEnoughFollowedItems } = useMyNews();
  const scmpExperiment = useExperimentCustomDimension();
  const { isAutoRefreshed, removeIsAutoRefreshed } = useIsAutoRefreshed();

  const variables = useMemo(() => {
    const userRole =
      asyncRosettaState?.result?.tracking?.userRole ??
      (isLoggedIn ? "authenticated user" : "anonymous user");
    const mixpanelId = asyncMixpanelState?.result?.get_distinct_id() as string | undefined;
    const adBlocker =
      asyncAdBlockState.result === undefined || asyncAdBlockState.result ? "yes" : "no";

    const product =
      (asyncRosettaState?.result?.user?.subscriptionProducts ?? []).length > 0
        ? first(asyncRosettaState?.result?.user?.subscriptionProducts)
        : null;

    return {
      adBlocker,
      autoRefresh: isAutoRefreshed ? "Yes" : "No",
      canonicalURL,
      isBot,
      loginType,
      myNewsSetup: hasEnoughFollowedItems ? "Yes" : "No",
      subscribe: {
        mixpanelId,
        product,
      },
      userRole,
      ...scmpExperiment,
    };
  }, [
    asyncAdBlockState.result,
    asyncMixpanelState?.result,
    asyncRosettaState?.result?.tracking?.userRole,
    asyncRosettaState?.result?.user?.subscriptionProducts,
    canonicalURL,
    hasEnoughFollowedItems,
    isAutoRefreshed,
    isBot,
    isLoggedIn,
    loginType,
    scmpExperiment,
  ]);
  const latestVariables = useSyncedRef(variables);

  // Keep pending page view information before all async states are fulfilled
  // Execute all pending page view when all async states are fulfilled and when the app is terminating
  // eslint-disable-next-line react/hook-use-state
  const [, setPendingPageViews] = useState<PendingPageView[]>([]);

  const isAllAsyncVariablesFulfilled = [
    asyncAdBlockState,
    asyncRosettaState,
    asyncMixpanelState,
  ].every(asyncState => asyncState?.error !== undefined || asyncState?.result !== undefined);
  const latestIsAllAsyncVariablesFulfilled = useSyncedRef(isAllAsyncVariablesFulfilled);

  const sendAllPendingPageView = useCallback(() => {
    setPendingPageViews(pendingPageViews => {
      pendingPageViews.forEach(pendingPageView => {
        const { canonicalURL, data } = pendingPageView;
        gtm.trackPageView({
          ...data,
          ...latestVariables.current,
          canonicalURL,
        });
      });
      return [];
    });
  }, [latestVariables]);

  // Execute all pending page view when all async states are fulfilled
  // Clear session storage value of `is_auto_refreshed`
  useEffect(() => {
    if (!isAllAsyncVariablesFulfilled) return;
    sendAllPendingPageView();
    removeIsAutoRefreshed();
  }, [isAllAsyncVariablesFulfilled, removeIsAutoRefreshed, sendAllPendingPageView]);

  // Execute all pending page view when the window is unloaded
  useEffect(() => {
    window.addEventListener("beforeunload", sendAllPendingPageView);
    return () => {
      window.removeEventListener("beforeunload", sendAllPendingPageView);
    };
  }, [latestVariables, sendAllPendingPageView]);

  const trackPageView: TrackPageViewFunction = useCallback(
    data => {
      if (latestIsAllAsyncVariablesFulfilled.current) {
        gtm.trackPageView({
          ...data,
          ...latestVariables.current,
        });
      } else {
        startTransition(() => {
          setPendingPageViews(pendingPageView => [
            ...pendingPageView,
            {
              canonicalURL: latestVariables.current.canonicalURL,
              data,
            },
          ]);
        });
      }
    },
    [latestIsAllAsyncVariablesFulfilled, latestVariables],
  );

  const setTracking = useSetAtom(trackingAtom);
  useEffect(() => {
    setTracking({ trackPageView });
  }, [setTracking, trackPageView]);
};

interface TrackImpressionEventByIntersectionOptions {
  intersectionThreshold?: number[];
  isSendGA4Tracking?: boolean;
  isSendGATracking?: boolean;
  shouldSendOnce?: boolean;
  shouldThrottle?: boolean;
}

interface TrackImpressionEventByIntersectionResult {
  captureTrackImpressionEventTargetElement: (element: HTMLElement | null) => void;
}

export interface UseTrackImpressionEventByIntersectionParameters {
  getEvent: (() => Event) | null;
  getGa4Event?: (() => GA4Event) | null;
  options?: TrackImpressionEventByIntersectionOptions;
}

export const useTrackImpressionEventByIntersection = (
  parameters: UseTrackImpressionEventByIntersectionParameters,
): TrackImpressionEventByIntersectionResult => {
  const { getEvent, getGa4Event, options } = parameters;
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [hasSent, setHasSent] = useState(false);

  const shouldSend = options?.shouldSendOnce ? !hasSent : true;
  const intersection = useIntersectionObserver(
    shouldSend && (getEvent || getGa4Event) ? targetElement : null,
    {
      threshold: options?.intersectionThreshold,
    },
  );

  const handleSend = useThrottledCallback(
    (event: Event | null, ga4Event: GA4Event | null) => {
      if (options?.isSendGATracking && event) {
        sendGATracking(event);
      }
      if (options?.isSendGA4Tracking && ga4Event) {
        sendGA4Tracking(ga4Event);
      }
      setHasSent(true);
    },
    [sendGATracking, sendGA4Tracking, setHasSent],
    options?.shouldThrottle ? 300 : 0,
    true,
  );

  useEffect(() => {
    if (!shouldSend || !intersection?.isIntersecting || (!getEvent && !getGa4Event)) return;
    handleSend(getEvent ? getEvent() : null, getGa4Event ? getGa4Event() : null);
  }, [getEvent, getGa4Event, shouldSend, intersection?.isIntersecting, handleSend]);

  useEffect(() => {
    setHasSent(false);
  }, [targetElement]);

  const captureTrackImpressionEventTargetElement = useCallback(
    (element: HTMLElement | null) => {
      setTargetElement(element);
    },
    [setTargetElement],
  );

  return { captureTrackImpressionEventTargetElement };
};
